import { useCallback } from "react";
import { InvoiceScheduleChange } from "@common/schemas/domains/invoice/invoice-schedule-change.schema";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getApiErrorMessage } from "@api/apiError";
import { SchedulePeriodLockedError } from "@api/scheduleErrors";
import { invalidateAfterLifecycleAction } from "@api/scheduleTemplate.api";
import { getScheduleLifecyclePeriodLockedError } from "@helpers/lock";
import { useScheduleLockedPeriodsDialog } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { buildInvoiceScheduleChangeToast } from "@components/accounting/schedules/change/scheduleChangeCopy";

/**
 * An invoice save carries its live schedules along; this tells the user what
 * happened to them, and routes a locked-month refusal to the locks dialog.
 */
export function useInvoiceEditScheduleFeedback() {
    const { workspaceId } = useScope();
    const { toast, error } = useToaster();
    const showLockedPeriods = useScheduleLockedPeriodsDialog();

    const notifyScheduleChanges = useCallback(
        (scheduleChanges: InvoiceScheduleChange[]) => {
            if (scheduleChanges.length === 0) {
                return;
            }
            invalidateAfterLifecycleAction(workspaceId);
            for (const change of scheduleChanges) {
                toast(buildInvoiceScheduleChangeToast(change), undefined, {
                    variant: "success",
                });
            }
        },
        [workspaceId, toast],
    );

    const handleSaveError = useCallback(
        (saveError: unknown) => {
            const lockedError =
                getScheduleLifecyclePeriodLockedError(saveError);
            if (
                lockedError &&
                showLockedPeriods(new SchedulePeriodLockedError(lockedError))
            ) {
                return;
            }
            error(getApiErrorMessage(saveError, "Failed to update invoices"));
        },
        [error, showLockedPeriods],
    );

    return { notifyScheduleChanges, handleSaveError };
}
